// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL
    ? marketplaceRootURL.replace(/\/$/, '')
    : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, {
    typeHandlers,
    verbose: appSettings.sdk.transitVerbose,
  });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') &&
    body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader
      ? contentTypeHeader.split(';')[0]
      : null;

    if (res.status >= 400) {
      return res.json().then(raw_data => {
        const data = { ...raw_data };
        if (!data.message && data.error) {
          data.message = data.error;
        }
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const put = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.PUT,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
    body,
  };

  return request(path, requestOptions);
};

const doDelete = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.DELETE,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const createApplication = body => {
  return post('/api/applications/apply', body);
};

export const fetchApplications = (query) => {
  const { page = 1, perPage = 20, status_in, type_in, sortBy = 'created_at', sortOrder = 'desc', email, first_name } = query || {};
  const queryParams = new URLSearchParams({ page: page.toString(), perPage: perPage.toString(), sortBy: sortBy.toString(), sortOrder: sortOrder.toString(), });
  if (status_in && Array.isArray(status_in) && status_in.length > 0) { status_in.forEach(status => queryParams.append('status_in', status)); }
  if (type_in && Array.isArray(type_in) && type_in.length > 0) { type_in.forEach(type => queryParams.append('type_in', type)); }
  if (email) queryParams.append('email', email);
  if (first_name) queryParams.append('first_name', first_name);
  return get(`/api/applications/?${queryParams.toString()}`);
};
export const acceptApplication = id =>
  post(`/api/applications/${id}/accept`, {});
export const rejectApplication = id =>
  post(`/api/applications/${id}/reject`, {});
export const resendInviteApplication = id =>
  post(`/api/applications/${id}/resend-invite`, {});

export const uploadApplicationFiles = (files, applicationId = null, isProfilePhoto = false) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  if (applicationId) {
    formData.append('application_id', applicationId);
  }
  if (isProfilePhoto) {
    formData.append('isProfilePhoto', isProfilePhoto);
  }

  return fetch(`${apiBaseUrl()}/api/applications/files/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: undefined,
  }).then(response => {
    if (!response.ok) {
      return response.json().then(errorData => {
        throw new Error(errorData.error || errorData.message || 'Upload failed');
      });
    }
    return response.json();
  });
};

export const checkEmailExistence = (email) => {
  const queryParams = new URLSearchParams({ email });
  return get(`/api/applications/email-exists?${queryParams.toString()}`);
};

// Fetch all files for an application
export const fetchApplicationFiles = applicationId => {
  return get(`/api/applications/${applicationId}/files`);
};


// ================ PROVIDER MANAGEMENT ================ //

// Get all profiles with optional filtering
export const getProfiles = (query = {}) => {
  const {
    page = 1,
    perPage = 20,
    status,
    email,
    phone,
    city,
    state,
    source,
    join_date_from,
    join_date_to,
    search,
  } = query;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
  });

  if (status) queryParams.append('status', status);
  if (email) queryParams.append('email', email);
  if (phone) queryParams.append('phone', phone);
  if (city) queryParams.append('city', city);
  if (state) queryParams.append('state', state);
  if (source) queryParams.append('source', source);
  if (join_date_from) queryParams.append('join_date_from', join_date_from);
  if (join_date_to) queryParams.append('join_date_to', join_date_to);
  if (search) queryParams.append('search', search);

  return get(`/api/profiles?${queryParams.toString()}`);
};

// Get single profile by ID
export const getProfile = (providerId) => {
  return get(`/api/profiles/${providerId}`);
};

// Create new profile
export const createProfile = (data) => {
  return post('/api/profiles', data);
};

// Update profile
export const updateProfile = (providerId, data) => {
  return put(`/api/profiles/${providerId}`, data);
};

export const updatePhoneNumber = (phoneNumber) => {
  return post('/api/profiles/update-phone', { phoneNumber });
}

// Delete profile
export const deleteProfile = (providerId) => {
  return doDelete(`/api/profiles/${providerId}`);
};

// Get profiles by status
export const getProvidersByStatus = (status = 'active') => {
  return get(`/api/profiles/status/${status}`);
};

// Find provider by email
export const findProviderByEmail = (email) => {
  return get(`/api/profiles/search/email/${email}`);
};

// Get provider by Sharetribe user_id
export const getProfileByUserId = (userId) => {
  return get(`/api/profiles?user_id=${userId}`);
};

// Get provider by Sharetribe user_id
export const getExtendedTransactionData = (transactionId) => {
  return get(`/api/leads/transactionData/${transactionId}`);
};

// ================ WEEKLY AVAILABILITY MANAGEMENT ================ //

// Get current user's weekly schedule in form format (for React form)
export const getMyWeeklyScheduleForForm = () => {
  return get('/api/availability-calendar/provider/weekly-schedule-form');
};

// Update current user's weekly schedule (for React form)
export const updateMyWeeklySchedule = (weeklyScheduleData) => {
  return put(
    '/api/availability-calendar/provider/weekly-schedule',
    { weeklySchedule: weeklyScheduleData },
  );
};

// Validate weekly schedule without saving
export const validateWeeklySchedule = (weeklyScheduleData) => {
  return post(
    '/api/availability-calendar/provider/weekly-schedule/validate',
    { weeklySchedule: weeklyScheduleData },
  );
};

// Get current user's weekly schedule summary
export const getMyWeeklyScheduleSummary = () => {
  return get('/api/availability-calendar/provider/weekly-schedule/summary');
};

// Get provider's weekly schedule in form format (by provider_id)
export const getProviderWeeklyScheduleForForm = (providerId) => {
  return get(`/api/availability-calendar/providers/${providerId}/weekly-schedule-form`);
};

// Update provider's weekly schedule (by provider_id)
export const updateProviderWeeklyScheduleComplete = (providerId, weeklyScheduleData) => {
  return put(
    `/api/availability-calendar/providers/${providerId}/weekly-schedule-complete`,
    { weeklySchedule: weeklyScheduleData },
  );
};

// Get provider's complete weekly schedule
export const getProviderWeeklySchedule = (providerId) => {
  return get(`/api/availability-calendar/providers/${providerId}/weekly-schedule`);
};

// Get availability for specific day of week
export const getProviderDayAvailability = (providerId, dayOfWeek) => {
  return get(`/api/availability-calendar/providers/${providerId}/weekly-schedule/day/${dayOfWeek}`);
};

// Create single weekly availability slot
export const createWeeklyAvailabilitySlot = (providerId, data) => {
  return post(`/api/availability-calendar/providers/${providerId}/weekly-schedule`, data);
};

// Bulk create weekly schedule
export const bulkCreateWeeklySchedule = (providerId, scheduleData) => {
  return post(`/api/availability-calendar/providers/${providerId}/weekly-schedule/bulk`, {
    schedule_data: scheduleData
  });
};

// Update weekly availability slot
export const updateWeeklyAvailabilitySlot = (slotId, data) => {
  return put(`/api/availability-calendar/weekly-schedule/${slotId}`, data);
};

// Delete weekly availability slot
export const deleteWeeklyAvailabilitySlot = (slotId) => {
  return doDelete(`/api/availability-calendar/weekly-schedule/${slotId}`);
};

// Delete all weekly schedule for provider
export const deleteProviderWeeklySchedule = (providerId) => {
  return doDelete(`/api/availability-calendar/providers/${providerId}/weekly-schedule`);
};

// Check for time conflicts in weekly schedule
export const checkWeeklyTimeConflict = (providerId, data) => {
  return post(`/api/availability-calendar/providers/${providerId}/weekly-schedule/check-conflict`, data);
};

// ================ CALENDAR AVAILABILITY MANAGEMENT ================ //

// Get provider calendar availability for date range
export const getMyCalendarAvailability = (startDate, endDate) => {
  const queryParams = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  if (!startDate && !endDate) {
    return get(`/api/availability-calendar/provider/calendar-availability`);
  }
  return get(`/api/availability-calendar/provider/calendar-availability?${queryParams.toString()}`);
};

export const getProviderCalendarAvailability = (providerId, startDate, endDate) => {
  const queryParams = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  return get(`/api/availability-calendar/providers/${providerId}/calendar-availability?${queryParams.toString()}`);
};

// Get calendar availability by type (available/unavailable)
export const getProviderCalendarAvailabilityByType = (providerId, availabilityType, startDate, endDate) => {
  const queryParams = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  return get(`/api/availability-calendar/providers/${providerId}/calendar-availability/${availabilityType}?${queryParams.toString()}`);
};

// Create calendar availability block
export const createMyCalendarAvailabilityBlock = (data) => {
  return post(`/api/availability-calendar/provider/calendar-availability`, data);
};

export const createCalendarAvailabilityBlock = (providerId, data) => {
  return post(`/api/availability-calendar/providers/${providerId}/calendar-availability`, data);
};

// Bulk create calendar availability blocks
export const bulkCreateCalendarAvailability = (providerId, availabilityBlocks) => {
  return post(`/api/availability-calendar/providers/${providerId}/calendar-availability/bulk`, {
    availability_blocks: availabilityBlocks
  });
};

// Update calendar availability block
export const updateCalendarAvailabilityBlock = (blockId, data) => {
  return put(`/api/availability-calendar/calendar-availability/${blockId}`, data);
};

// Delete calendar availability block
export const deleteCalendarAvailabilityBlock = (blockId) => {
  return doDelete(`/api/availability-calendar/calendar-availability/${blockId}`);
};

// Delete provider calendar availability (with optional date range)
export const deleteProviderCalendarAvailability = (providerId, startDate = null, endDate = null) => {
  let url = `/api/availability-calendar/providers/${providerId}/calendar-availability`;

  if (startDate && endDate) {
    const queryParams = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    url += `?${queryParams.toString()}`;
  }

  return doDelete(url);
};

// Check for overlapping calendar availability
export const checkCalendarAvailabilityOverlap = (providerId, data) => {
  return post(`/api/availability-calendar/providers/${providerId}/calendar-availability/check-overlap`, data);
};

// Get upcoming availability
export const getUpcomingAvailability = (providerId, daysAhead = 30) => {
  const queryParams = new URLSearchParams({
    days_ahead: daysAhead.toString(),
  });
  return get(`/api/availability-calendar/providers/${providerId}/calendar-availability/upcoming?${queryParams.toString()}`);
};

// Get availability statistics
export const getProviderAvailabilityStats = (providerId, startDate, endDate) => {
  const queryParams = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  return get(`/api/availability-calendar/providers/${providerId}/calendar-availability/stats?${queryParams.toString()}`);
};

// ================ AVAILABILITY CHECKING ================ //

// Check if provider is available for specific datetime range
export const checkProviderAvailability = (providerId, startDatetime, endDatetime) => {
  return post(`/api/availability-calendar/providers/${providerId}/check-availability`, {
    start_datetime: startDatetime,
    end_datetime: endDatetime,
  });
};

// Get provider availability for date range with optional time slots
export const getProviderAvailabilityForDateRange = (providerId, startDate, endDate, timeSlots = null) => {
  const queryParams = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });

  if (timeSlots) {
    queryParams.append('time_slots', JSON.stringify(timeSlots));
  }

  return get(`/api/availability-calendar/providers/${providerId}/availability-range?${queryParams.toString()}`);
};

// Batch check availability for multiple providers
export const batchCheckProviderAvailability = (providerData, startDatetime, endDatetime) => {
  return post('/api/availability-calendar/providers/batch-check-availability', {
    provider_data: providerData,
    start_datetime: startDatetime,
    end_datetime: endDatetime,
  });
};

// ================ CONVENIENCE METHODS ================ //

// Get all availability data for a provider (weekly + calendar)
export const getProviderFullAvailability = async (providerId, startDate, endDate) => {
  try {
    const [weeklySchedule, calendarAvailability] = await Promise.all([
      getProviderWeeklySchedule(providerId),
      getProviderCalendarAvailability(providerId, startDate, endDate),
    ]);

    return {
      provider_id: providerId,
      weekly_schedule: weeklySchedule,
      calendar_availability: calendarAvailability,
    };
  } catch (error) {
    console.error('Error fetching full provider availability:', error);
    throw error;
  }
};

// Check availability for common time slots (9am-5pm business hours)
export const checkBusinessHoursAvailability = (providerId, startDate, endDate) => {
  const businessHoursSlots = [
    { start_time: '09:00:00', end_time: '17:00:00' }
  ];

  return getProviderAvailabilityForDateRange(providerId, startDate, endDate, businessHoursSlots);
};

// Get available providers for a specific time slot
export const getAvailableProviders = async (providerIds, startDatetime, endDatetime) => {
  try {
    const result = await batchCheckProviderAvailability(providerIds, startDatetime, endDatetime);
    return {
      ...result,
      available_provider_ids: result.results
        .filter(r => r.available)
        .map(r => r.provider_id),
      unavailable_provider_ids: result.results
        .filter(r => !r.available)
        .map(r => r.provider_id),
    };
  } catch (error) {
    console.error('Error getting available providers:', error);
    throw error;
  }
};


// Extended profile data fetch
export const getExtendedProfileData = () => {
  return get(`/api/profile-extended-data`);
}
export const getListingProfileData = (listingId) => {
  return get(`/api/profiles/profile-for-listing/${listingId}`);
}
export const syncListingWithProfile = () => {
  return post(`/api/profiles/sync-listing-with-profile`, {});
}

export const getLeads = async leadData => {
  return get('/api/leads', leadData);
};

export const createLead = async leadData => {
  return post('/api/leads', leadData);
};

export const createApplicationImage = (img) => {
  return post('/api/applications/pet_images', img);
};

export const fetchApplicationImages = (applicationId) => {
  return get(`/api/applications/${applicationId}/pet_images`);
};

// Pet Management
export const fetchPets = () => {
  return get('/api/pets');
};

export const getPet = (id) => {
  return get(`/api/pets/${id}`);
};

export const createPet = (data) => {
  return post('/api/pets', data);
};

export const updatePet = (id, data) => {
  return put(`/api/pets/${id}`, data);
};

export const deletePet = (id) => {
  return doDelete(`/api/pets/${id}`);
};

export const getPetDataForCustomers = (customerIds) => {
  return post('/api/pets/pet-data-for-customers', { customer_ids: customerIds });
};


export const uploadPetFiles = (files, petId = null, isPrimary = false) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  if (petId) {
    formData.append('application_id', petId);
  }
  if (isPrimary) {
    formData.append('isPrimary', isPrimary);
  }

  return fetch(`${apiBaseUrl()}/api/pets/${petId}/files/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: undefined,
  }).then(response => {
    if (!response.ok) {
      return response.json().then(errorData => {
        throw new Error(errorData.error || errorData.message || 'Upload failed');
      });
    }
    return response.json();
  });
};

// Fetch all files for a pet
export const fetchPetFiles = petId => {
  return get(`/api/pets/${petId}/files`);
};

export const createPetImage = (petId, img) => {
  return post(`/api/pets/${petId}/pet_images`, img);
};

export const updatePetImage = (petId, id, data) => {
  return put(`/api/pets/${petId}/pet_images/${id}`, data);
};

export const deletePetImage = (petId, id) => {
  return doDelete(`/api/pets/${petId}/pet_images/${id}`);
};
export const fetchPetImages = (petId) => {
  return get(`/api/pets/${petId}/pet_images`);
};

// ================ SMS NOTIFICATIONS ================ //

/**
 * Sends SMS notification to provider
 * @param {Object} params
 * @param {string} params.providerId - Provider's user ID
 * @param {string} params.smsText - SMS message text
 * @param {string} params.phoneNumber - Provider's phone number (optional)
 * @returns {Promise<Object>} Response from SMS API
 */
export const sendSMSNotification = (params) => {
  return post('/api/sms-notification', params);
};

/**
 * Sends MMS notification to provider with image
 * @param {Object} params
 * @param {string} params.providerId - Provider's user ID
 * @param {string} params.mmsText - MMS message text
 * @param {string} params.imageUrl - URL of the image to send
 * @param {Object} params.imageData - Image data object with metadata
 * @param {string} params.phoneNumber - Provider's phone number (optional)
 * @returns {Promise<Object>} Response from MMS API
 */
export const sendMMSNotification = (params) => {
  return post('/api/sms-notification/mms', params);
};

// Update profile photo in profiles table
export const updateProfilePhoto = (userId, primaryPhoto, sharetribeImageId = null) => {
  const payload = {
    user_id: userId,
    primary_photo: primaryPhoto,
  };

  if (sharetribeImageId) {
    payload.sharetribe_image_id = sharetribeImageId;
  }

  return post('/api/profiles/update-photo', payload);
};

// Upload profile photo to S3
export const uploadProfilePhoto = (file) => {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('isProfilePhoto', 'true');

  return fetch(`${apiBaseUrl()}/api/applications/files/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: undefined,
  }).then(response => {
    if (!response.ok) {
      return response.json().then(errorData => {
        throw new Error(errorData.error || errorData.message || 'Upload failed');
      });
    }
    return response.json();
  }).then(data => data.files[0]); // Return the first uploaded file
};

export const requestPhoneVerificationCode = () => {
  return post(`/api/profiles/request-phone-verification`, {});
};

export const verifyPhoneVerificationCode = (code) => {
  return post(`/api/profiles/verify-phone-code`, { code });
};
